/**
 * Shared CORS proxy fetch utility.
 *
 * Centralises proxy selection, ordering, retry-with-backoff for the
 * Cloudflare Worker, and sequential fallback through CORS proxies.
 *
 * Proxy order (most → least reliable):
 *   1. Cloudflare Worker  (our own — retried once on transient failure)
 *   2. proxy.cors.sh      (paid API-key service — reliable when key is set)
 *   3. corsproxy.io       (99.9 % uptime, 10 K req/month free tier)
 *   4. allorigins.win     (free, no key, fair reliability)
 *   5. codetabs.com       (free, often slow / down — last resort)
 */

const PROPERTY_PROXY_URL = process.env.REACT_APP_PROPERTY_PROXY_URL || '';
const CORS_SH_API_KEY = process.env.REACT_APP_CORS_SH_API_KEY || '';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Upstream statuses that mean "this listing is gone", not "the proxy is down". */
const NOT_FOUND_STATUSES = new Set([404, 410]);

/**
 * Error thrown when our own proxy (Cloudflare Worker / buysell-fetcher)
 * reports the target listing no longer exists. Callers can check
 * `error.code === 'not_found'` (or `isNotFoundError`) to mark a property
 * expired instead of reporting a proxy outage.
 */
export class ProxyNotFoundError extends Error {
  constructor(message) {
    super(message || 'Listing not found (HTTP 404): the page has been removed or expired.');
    this.name = 'ProxyNotFoundError';
    this.code = 'not_found';
  }
}

export const isNotFoundError = (error) => !!error && error.code === 'not_found';

/**
 * Build the ordered list of proxy entries for a given target URL.
 * Each entry has { url, extraHeaders? }.
 * @param {string} targetUrl - The real URL to fetch
 * @returns {Array<{url: string, extraHeaders?: Object}>}
 */
export function buildProxyEntries(targetUrl) {
  const encoded = encodeURIComponent(targetUrl);
  const entries = [];

  if (PROPERTY_PROXY_URL) {
    entries.push({ url: `${PROPERTY_PROXY_URL}?url=${encoded}` });
  }

  if (CORS_SH_API_KEY) {
    entries.push({
      url: `https://proxy.cors.sh/${targetUrl}`,
      extraHeaders: { 'x-cors-api-key': CORS_SH_API_KEY },
    });
  }

  entries.push(
    { url: `https://corsproxy.io/?${encoded}` },
    { url: `https://api.allorigins.win/raw?url=${encoded}` },
    { url: `https://api.codetabs.com/v1/proxy?quest=${encoded}` }
  );

  return entries;
}

/**
 * Fetch a URL through CORS proxies with automatic fallback.
 *
 * The Cloudflare Worker (index 0 when configured) is retried once with a 1 s
 * delay before falling through to the other proxies.
 *
 * @param {string}   targetUrl               - The real URL to fetch
 * @param {Object}   [options]
 * @param {number}   [options.timeout=15000]  - Per-request timeout (ms)
 * @param {'text'|'json'} [options.responseType='text'] - How to read the body
 * @param {Object}   [options.headers={}]     - Extra fetch headers
 * @param {function} [options.validate]       - Optional (body) → bool. Return false to skip.
 * @param {string}   [options.label='Proxy']  - Log prefix
 * @returns {Promise<*>} Parsed response body (string or object)
 * @throws {ProxyNotFoundError} when our own proxy reports the target is gone (404/410)
 * @throws {Error} "All proxy services are currently unavailable…" otherwise
 */
export async function fetchViaProxies(targetUrl, options = {}) {
  const {
    timeout = 15000,
    responseType = 'text',
    headers = {},
    validate,
    label = 'Proxy',
  } = options;

  const entries = buildProxyEntries(targetUrl);
  const workerConfigured = !!PROPERTY_PROXY_URL;

  for (let i = 0; i < entries.length; i++) {
    const { url, extraHeaders } = entries[i];
    const isWorker = workerConfigured && i === 0;
    const maxAttempts = isWorker ? 2 : 1;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        if (attempt > 0) {
          await sleep(1000);
          console.log(`[${label}] Retrying Worker (attempt ${attempt + 1})`);
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          signal: controller.signal,
          headers: {
            Accept:
              responseType === 'json'
                ? 'application/json, */*'
                : 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
            ...headers,
            ...extraHeaders,
          },
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          // Only our own proxy is trusted for a 404: it forwards the target's
          // status (and detects "redirected to homepage"), whereas public CORS
          // proxies 404 for their own reasons. Don't retry or fall through —
          // the listing is gone.
          if (isWorker && NOT_FOUND_STATUSES.has(response.status)) {
            console.warn(`[${label}] Target reports listing not found (HTTP ${response.status})`);
            throw new ProxyNotFoundError();
          }
          console.warn(`[${label}] Proxy ${i + 1} HTTP ${response.status}`);
          if (attempt < maxAttempts - 1) continue;
          break;
        }

        const body =
          responseType === 'json'
            ? await response.json()
            : await response.text();

        if (validate && !validate(body)) {
          console.warn(`[${label}] Proxy ${i + 1} failed validation`);
          if (attempt < maxAttempts - 1) continue;
          break;
        }

        const suffix = attempt > 0 ? ` (attempt ${attempt + 1})` : '';
        console.log(`[${label}] OK via proxy ${i + 1}/${entries.length}${suffix}`);
        return body;
      } catch (error) {
        if (isNotFoundError(error)) throw error;
        if (error.name === 'AbortError') {
          console.warn(`[${label}] Proxy ${i + 1} timed out`);
        } else {
          console.error(`[${label}] Proxy ${i + 1} error:`, error.message);
        }
        if (attempt < maxAttempts - 1) continue;
        break;
      }
    }
  }

  throw new Error(
    'All proxy services are currently unavailable. ' +
      'Please try again later or input property data manually.'
  );
}
